import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import {
  Image as ImageIcon,
  ArrowLeft,
  RefreshCw,
  Search,
  Grid,
  List,
  Camera,
  FileImage,
  Loader,
  Plus,
  AlertTriangle
} from 'lucide-react';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

// Add CSS for spin animation
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

// PhotoDisplay component with error handling
const PhotoDisplay = ({ photo, currentThemeName, theme }) => {
  const [imageError, setImageError] = React.useState(false);
  const [imageLoading, setImageLoading] = React.useState(true);

  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  if (imageError) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 220,
        background: currentThemeName === 'dark' ? 'rgba(15,23,42,0.65)' : '#f1f5f9',
        color: currentThemeName === 'dark' ? '#94a3b8' : '#475569',
        border: currentThemeName === 'dark' ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(239, 68, 68, 0.2)'
      }}>
        <AlertTriangle size={40} style={{ marginBottom: 12, opacity: 0.7, color: '#ef4444' }} />
        <div style={{ fontSize: 14, fontWeight: 600, color: '#ef4444', marginBottom: 4 }}>Photo failed to load</div>
        <div style={{ fontSize: 12, opacity: 0.75, textAlign: 'center', padding: '0 16px' }}>
          URL: {photo.url || 'N/A'}
        </div>
        <div style={{ fontSize: 11, opacity: 0.6, marginTop: 8 }}>
          Try replacing the photo or check the file path.
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: 220, overflow: 'hidden' }}>
      {imageLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: currentThemeName === 'dark' ? 'rgba(15,23,42,0.65)' : '#f1f5f9',
          zIndex: 1
        }}>
          <Loader size={24} style={{ animation: 'spin 1s linear infinite', opacity: 0.5 }} />
        </div>
      )}
      <img
        src={photo.url}
        alt={photo.name || 'Photo'}
        onError={handleImageError}
        onLoad={handleImageLoad}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: imageError ? 'none' : 'block'
        }}
      />
      {!imageError && (
        <div style={{ position: 'absolute', bottom: 12, left: 12, background: 'rgba(15,23,42,0.75)', color: 'white', padding: '6px 12px', borderRadius: 12, fontSize: 12 }}>
          {photo.name || 'Unnamed photo'}
        </div>
      )}
    </div>
  );
};

function ContentEditPage() {
  const { theme, currentThemeName } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const API_BASE_URL = getApiBaseUrl();
  const [activeCategory, setActiveCategory] = useState('businessHelpTopics');
  const [photos, setPhotos] = useState({
    businessHelpTopics: [],
    primaryBusiness: [],
    secondBusiness: []
  });
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  // Upload/replace states removed
  const [uploading, setUploading] = useState(false);
  const [pendingTopicName, setPendingTopicName] = useState(null);
  const [uploadTargetTopic, setUploadTargetTopic] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('businessTypes');
  const fileInputRef = React.useRef(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const replaceFileInputRef = React.useRef(null);
  const [replacingId, setReplacingId] = useState(null);
  const pendingTopicRef = React.useRef(null);
  const uploadTargetTopicRef = React.useRef(null);
  // Log box state
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(true);
  const addLog = (msg, level = 'info') => {
    try {
      setLogs(prev => {
        const entry = { ts: new Date().toISOString(), level, msg: String(msg) };
        // keep last 200 logs
        return [...prev.slice(-199), entry];
      });
    } catch (e) {
      // no-op
    }
  };

  const isDarkMode = currentThemeName?.toLowerCase().includes('dark');
  const baseTextColor = isDarkMode ? '#f9fafb' : (theme.textPrimary || '#111827');
  const mutedTextColor = isDarkMode ? 'rgba(255,255,255,0.72)' : (theme.textSecondary || '#4b5563');
  const surfaceBackground = isDarkMode ? 'rgba(17, 24, 39, 0.9)' : (theme.cardBackground || '#ffffff');
  const pageBackground = isDarkMode ? '#0f172a' : (theme.bodyBackground || '#f3f4f6');

  const fetchTopics = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');

      const res = await fetch(`${API_BASE_URL}/api/admin/content/topics`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!res.ok) {
        const text = await res.text();
        addLog(`fetchTopics: http ${res.status} ${text}`, 'error');
        throw new Error(`Failed to load topics: ${res.status}`);
      }

      const data = await res.json();
      const loadedTopics = Array.isArray(data?.topics) ? data.topics : [];
      setTopics(loadedTopics);
      addLog(`fetchTopics: loaded ${loadedTopics.length} topics`);
    } catch (error) {
      console.error('Error fetching topics:', error);
      addLog(`fetchTopics: error ${error?.message || error}`, 'error');
    }
  };

  const normalizeName = (value) =>
    (value || '')
      .normalize('NFKC')
      .replace(/\s+/g, ' ')
      .replace(/[^a-zA-Z0-9\-\s_&]+/g, '') // Keep & character for topic matching
      .trim();


  // UI will show only two tabs. "Business Types" merges primary + second under the hood
  const categories = [
    { id: 'businessHelpTopics', name: 'Business Help Topics', icon: FileImage },
    { id: 'businessTypes', name: 'Business Types', icon: Camera }
  ];

  const topicPhotoMap = useMemo(() => {
    const map = new Map();
    (photos.businessHelpTopics || []).forEach(photo => {
      const key = normalizeName(photo.name);
      if (!map.has(key)) {
        map.set(key, photo);
      } else {
        // If multiple photos with same name, keep the most recent one
        const existing = map.get(key);
        const parseDate = (dateStr) => {
          if (!dateStr) return new Date(0);
          const parsed = new Date(dateStr);
          return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
        };
        const existingDate = parseDate(existing.uploadDate || existing.created_at);
        const currentDate = parseDate(photo.uploadDate || photo.created_at);
        if (currentDate > existingDate) {
          map.set(key, photo);
        }
      }
    });
    return map;
  }, [photos.businessHelpTopics]);

  const topicEntries = useMemo(() => {
    if (!Array.isArray(topics)) return [];
    return topics.map(topic => {
      const key = normalizeName(topic.name);
      const photo = topicPhotoMap.get(key) || null;
      return {
        topic,
        photo,
        normalizedName: key,
        hasPhoto: Boolean(photo)
      };
    });
  }, [topics, topicPhotoMap]);

  const handleTopicPhotoUpload = (entry) => {
    const topicName = entry?.topic?.name || pendingTopicName;
    setUploadTargetTopic(entry || null);
    uploadTargetTopicRef.current = entry || null;
    setPendingTopicName(topicName || null);
    pendingTopicRef.current = topicName || null;
    addLog(`Topic photo upload initiated: topic="${topicName || 'none'}"`, topicName ? 'info' : 'error');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredTopicEntries = useMemo(() => {
    if (!normalizedSearch) return topicEntries;
    return topicEntries.filter(entry => {
      const name = entry.topic?.name || '';
      const description = entry.topic?.description || '';
      return `${name} ${description}`.toLowerCase().includes(normalizedSearch);
    });
  }, [topicEntries, normalizedSearch]);

  const businessTypePhotos = useMemo(() => {
    if (activeCategory !== 'businessTypes') return [];
    return [
      ...(photos.primaryBusiness || []),
      ...(photos.secondBusiness || [])
    ];
  }, [activeCategory, photos.primaryBusiness, photos.secondBusiness]);

  const filteredBusinessPhotos = useMemo(() => {
    if (activeCategory !== 'businessTypes') return [];
    if (!normalizedSearch) return businessTypePhotos;
    return businessTypePhotos.filter(photo => (photo.name || '').toLowerCase().includes(normalizedSearch));
  }, [activeCategory, businessTypePhotos, normalizedSearch]);

  // Helper functions for API calls
  const createTopic = async (name) => {
    const token = localStorage.getItem('token');

    const res = await fetch(`${API_BASE_URL}/api/admin/content/topics`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ name }),
      credentials: 'include'
    });

    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  const createCategory = async (name) => {
    const token = localStorage.getItem('token');

    const res = await fetch(`${API_BASE_URL}/api/admin/content/categories`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ name }),
      credentials: 'include'
    });

    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  const getBusinessImagesMap = () => {
    try {
      const context = require.context('../assets/businessPhoto', false, /\.(png|jpe?g|webp)$/);
      const map = {};
      context.keys().forEach((key) => {
        const fileName = key.replace('./', '');
        map[fileName.toLowerCase()] = context(key);
      });
      return map;
    } catch (e) {
      // If bundler doesn't support require.context, fallback to empty map
      console.warn('Business images context not available:', e?.message || e);
      return {};
    }
  };

  const handleChangePhotoClick = (photo) => {
    if (!photo?.id || !Number.isInteger(photo.id)) {
      addLog(`Change Photo blocked: item is not DB-backed (id=${photo?.id})`, 'error');
      return;
    }
    addLog(`Change Photo clicked for id=${photo.id}`);
    setReplacingId(photo.id);
    if (replaceFileInputRef.current) {
      replaceFileInputRef.current.value = '';
      replaceFileInputRef.current.click();
    }
  };

  const handleReplaceFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !replacingId) return;

    // Check file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      addLog(`Replace cancelled: file too large (${(file.size / 1024 / 1024).toFixed(1)}MB > 50MB)`, 'error');
      alert(`File too large! Maximum size is 50MB. Your file is ${(file.size / 1024 / 1024).toFixed(1)}MB.`);
      setReplacingId(null);
      return;
    }

    try {
      addLog(`Replace: start id=${replacingId}, file=${file.name} (${file.type}, ${(file.size / 1024 / 1024).toFixed(1)}MB)`);
      setUploading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const form = new FormData();
      form.append('photo', file);
      const res = await fetch(`${API_BASE_URL}/api/admin/content/photos/${replacingId}/replace`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: form,
        credentials: 'include'
      });
      if (!res.ok) {
        const text = await res.text();
        addLog(`Replace failed: ${res.status} ${text}`, 'error');
        throw new Error(`Replace failed: ${res.status} ${text}`);
      }
      addLog('Replace: success');
      await fetchPhotos();
    } catch (err) {
      console.error('Replace error:', err);
      addLog(`Replace error: ${err?.message || err}`, 'error');
    } finally {
      setUploading(false);
      setReplacingId(null);
    }
  };

  const editPhoto = async (photo) => {
    if (!photo?.id || !Number.isInteger(photo.id)) {
      addLog(`Edit blocked: item is not DB-backed (id=${photo?.id})`, 'error');
      return;
    }
    try {
      addLog(`Edit: start id=${photo.id}`);
      const newName = window.prompt('Enter new name', photo.name || '');
      if (newName === null) return; // cancelled
      const allowedCats = ['businessHelpTopics', 'primaryBusiness', 'secondBusiness'];
      const newCatInput = window.prompt(`Enter category (${allowedCats.join(', ')})`, photo.category || 'primaryBusiness');
      if (newCatInput === null) return; // cancelled
      const newCategory = allowedCats.includes(newCatInput) ? newCatInput : photo.category;
      setSavingEdit(true);
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/admin/content/photos/${photo.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newName, category: newCategory }),
        credentials: 'include'
      });
      if (!res.ok) {
        const txt = await res.text();
        addLog(`Edit failed: ${res.status} ${txt}`, 'error');
        throw new Error(`Edit failed: ${res.status} ${txt}`);
      }
      addLog('Edit: success');
      await fetchPhotos();
    } catch (e) {
      console.error('Edit error:', e);
      addLog(`Edit error: ${e?.message || e}`, 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleUploadClick = () => {
    addLog('Upload button clicked');
    setUploadTargetTopic(null);
    uploadTargetTopicRef.current = null;
    setPendingTopicName(null);
    pendingTopicRef.current = null;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    // Check file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      addLog(`Upload cancelled: file too large (${(file.size / 1024 / 1024).toFixed(1)}MB > 50MB)`, 'error');
      alert(`File too large! Maximum size is 50MB. Your file is ${(file.size / 1024 / 1024).toFixed(1)}MB.`);
      return;
    }

    try {
      addLog(`Upload: selected file ${file.name} (${file.type}, ${(file.size / 1024 / 1024).toFixed(1)}MB)`);
      setUploading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');

      const form = new FormData();
      form.append('photo', file);
      const pendingTopicNameValue = pendingTopicRef.current;
      const uploadTarget = uploadTargetTopicRef.current;
      const isTopicUpload = Boolean(uploadTarget?.topic);
      // Map UI category to backend category names; for now treat businessTypes as primaryBusiness unless a topic is explicitly targeted
      const cat = isTopicUpload
        ? 'businessHelpTopics'
        : activeCategory === 'businessTypes'
          ? 'primaryBusiness'
          : 'businessHelpTopics';

      if (cat === 'businessHelpTopics' && !pendingTopicNameValue) {
        addLog('Upload cancelled: select a Business Help Topic before uploading.', 'error');
        alert('Please choose a Business Help Topic card before uploading a photo.');
        setUploading(false);
        return;
      }

      form.append('category', cat);

      const normalizedTopicName = normalizeName(pendingTopicNameValue);

      const sanitizedBase = (file.name || 'uploaded')
        .replace(/\.[^.]+$/, '')
        .replace(/[^a-zA-Z0-9\-\s_]+/g, '')
        .trim();

      const nameForPhoto = normalizedTopicName
        ? (pendingTopicNameValue || sanitizedBase || 'uploaded')
        : sanitizedBase || 'uploaded';

      const normalizedPending = normalizeName(pendingTopicNameValue);
      const matchedTopicEntry = normalizedPending ? topicEntries.find(entry => entry.normalizedName === normalizedPending) : null;
      const matchedPhotoFromTopic = matchedTopicEntry?.photo || null;

      form.append('name', nameForPhoto);
      if (cat === 'businessHelpTopics' && matchedPhotoFromTopic && matchedPhotoFromTopic.id) {
        form.append('replacePhotoId', matchedPhotoFromTopic.id);
      }

      addLog(`Upload: category=${cat}, isTopicUpload=${isTopicUpload}, topic="${pendingTopicNameValue || 'n/a'}"`, 'info');

      const res = await fetch(`${API_BASE_URL}/api/admin/content/photos/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: form,
        credentials: 'include'
      });
      if (!res.ok) {
        const text = await res.text();
        addLog(`Upload failed: ${res.status} ${text}`, 'error');
        throw new Error(`Upload failed: ${res.status} ${text}`);
      }
      addLog('Upload: success');
      if (cat === 'businessHelpTopics') {
        await fetchTopics();
      }
      await fetchPhotos({ skipLoading: true });
    } catch (err) {
      console.error('Upload error:', err);
      addLog(`Upload error: ${err?.message || err}`, 'error');
    } finally {
      setUploading(false);
      uploadTargetTopicRef.current = null;
      pendingTopicRef.current = null;
      setUploadTargetTopic(null);
      setPendingTopicName(null);
    }
  };

  // Replace flow removed

  // File picker removed

  // replacePhoto removed

  // handleReplaceFileChange removed

  // importExistingAssetsToDB removed
  const businessImagesMap = getBusinessImagesMap();

  useEffect(() => {
    addLog('fetchPhotos: initial load');
    fetchTopics();
    fetchPhotos();
  }, []);

  const fetchPhotos = async (options = {}) => {
    const skipLoading = Boolean(options.skipLoading);
    try {
      if (!skipLoading) {
        setLoading(true);
      }
      addLog('fetchPhotos: start');
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/api/admin/content/photos`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Raw API response:', data);
        addLog(`fetchPhotos: ok`);
        
        // Build full URLs and add cache busting
        const processPhotos = (photos) => {
          if (!photos || photos.length === 0) return photos;
          return photos.map(photo => {
            const version = photo.updatedAt ? new Date(photo.updatedAt).getTime() : Date.now();
            // Build full URL: if relative path, prepend API base URL
            let fullUrl = photo.url || '';
            // Only prepend API_BASE_URL if URL is relative (starts with /) and not already a full URL
            if (fullUrl && fullUrl.startsWith('/') && !fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
              fullUrl = `${API_BASE_URL}${fullUrl}`;
            }
            // If URL is empty or invalid, skip cache busting
            if (!fullUrl) {
              console.warn('⚠️ Photo has no URL:', photo);
              return { ...photo, url: '' };
            }
            const separator = fullUrl.includes('?') ? '&' : '?';
            return {
              ...photo,
              url: `${fullUrl}${separator}v=${version}`
            };
          });
        };
        
        // If DB empty for business categories, show local assets as preview
        const localBusiness = await loadExistingBusinessPhotos();
        const processedPhotos = {
          businessHelpTopics: processPhotos(data.businessHelpTopics || []),
          primaryBusiness: (data.primaryBusiness && data.primaryBusiness.length > 0) ? processPhotos(data.primaryBusiness) : localBusiness,
          secondBusiness: (data.secondBusiness && data.secondBusiness.length > 0) ? processPhotos(data.secondBusiness) : localBusiness
        };
        
        console.log('✅ Photos with cache busting:', processedPhotos);
        addLog(`fetchPhotos: set ${processedPhotos.businessHelpTopics?.length || 0} help topic, ${processedPhotos.primaryBusiness?.length || 0} primary, ${processedPhotos.secondBusiness?.length || 0} second photos`);
        setPhotos(processedPhotos);
      } else {
        addLog(`fetchPhotos: http ${response.status}`, 'error');
        // Fallback to existing business photos for now
        const businessPhotos = await loadExistingBusinessPhotos();
        setPhotos({
          businessHelpTopics: [],
          primaryBusiness: businessPhotos,
          secondBusiness: businessPhotos
        });
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
      addLog(`fetchPhotos: error ${error?.message || error}`, 'error');
    } finally {
      if (!skipLoading) {
        setLoading(false);
      }
      addLog('fetchPhotos: finished');
    }
  };

  const loadExistingBusinessPhotos = async () => {
    // Load existing business photos from assets
    const businessTypes = [
      'HVAC', 'accounting-firm', 'art-gallery', 'auto-repair', 'bakery', 'barbershop',
      'beauty-salon', 'bookstore', 'carpentry', 'cleaning-service', 'coffee-shop',
      'construction', 'consulting', 'dance-studio', 'daycare', 'dental-office',
      'electrical', 'florist', 'food-truck', 'gym', 'insurance-agency',
      'jewelry-store', 'landscaping', 'laundry', 'law-firm', 'marketing-agency',
      'music-school', 'nail-salon', 'other', 'painting', 'pet-grooming',
      'photography-studio', 'plumbing', 'real-estate', 'restaurant', 'retail-store',
      'roofing', 'tattoo-parlor', 'tech-startup', 'vintage-shop', 'web-design', 'yoga-studio'
    ];

    // Only include if the image actually exists in assets and resolve the correct URL via context
    const items = [];
    for (const type of businessTypes) {
      const candidates = [
        `${type}.jpg`,
        `${type}.jpeg`,
        `${type}.png`,
        `${type.toLowerCase()}.jpg`,
        `${type.toLowerCase()}.jpeg`,
        `${type.toLowerCase()}.png`,
      ];
      let foundFile = null;
      for (const c of candidates) {
        const key = c.toLowerCase();
        if (businessImagesMap[key]) {
          foundFile = c;
          break;
        }
      }
      if (foundFile) {
        const url = businessImagesMap[foundFile.toLowerCase()];
        items.push({
          id: type,
          name: type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          filename: foundFile,
          url,
          category: 'business',
          size: 'Unknown',
          uploadDate: 'Existing'
        });
      }
    }
    return items;
  };

  // handleFileUpload removed

  // uploadPhoto removed

  // resizeImage helper removed

  const deletePhoto = async (photoId) => {
    if (!photoId || !Number.isInteger(photoId)) {
      addLog(`Delete blocked: item is not DB-backed (id=${photoId})`, 'error');
      return; // Prevent deleting non-DB assets
    }
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    try {
      addLog(`Delete: start id=${photoId}`);
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');

      const response = await fetch(`${API_BASE_URL}/api/admin/content/photos/${photoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        addLog('Delete: success');
        fetchPhotos();
      } else {
        addLog(`Delete failed: ${response.status}`, 'error');
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      addLog(`Delete error: ${error?.message || error}`, 'error');
      // Notification removed
    }
  };

  // showNotification removed

  const getCategoryCount = (catId) => {
    if (catId === 'businessTypes') {
      return filteredBusinessPhotos.length;
    }
    return topicEntries.length;
  };

  const PhotoCard = ({ photo }) => (
    <div style={{
      background: currentThemeName === 'dark' 
        ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)'
        : currentThemeName === 'beige'
        ? 'linear-gradient(135deg, rgba(245, 245, 220, 0.9) 0%, rgba(250, 235, 215, 0.95) 100%)'
        : '#ffffff',
      border: currentThemeName === 'dark'
        ? '1px solid rgba(59, 130, 246, 0.2)'
        : currentThemeName === 'beige'
        ? '1px solid rgba(139, 69, 19, 0.15)'
        : '1px solid #e5e7eb',
      borderRadius: 16,
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      boxShadow: currentThemeName === 'dark' 
        ? 'none' 
        : currentThemeName === 'beige' 
        ? '0 1px 2px rgba(0,0,0,0.04)'
        : '0 1px 2px rgba(0,0,0,0.06)'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-4px)';
      e.currentTarget.style.boxShadow = currentThemeName === 'dark'
        ? '0 12px 40px rgba(0, 0, 0, 0.4)'
        : '0 10px 30px rgba(0,0,0,0.1)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}
    >
      <div style={{
        width: '100%',
        height: 200,
        position: 'relative',
        overflow: 'hidden',
        background: '#f3f4f6'
      }}>
        <img 
          src={photo.url} 
          alt={photo.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          onError={(e) => {
            console.error(`Failed to load image: ${photo.url}`);
            e.target.style.display = 'none';
            e.target.parentElement.innerHTML = `
              <div style="display: flex; align-items: center; justify-content: center; height: 100%; flex-direction: column; color: #6b7280;">
                <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <div style="margin-top: 8px; font-size: 12px;">Image not found</div>
                <div style="font-size: 10px; opacity: 0.7; margin-top: 4px; max-width: 200px; word-break: break-all;">${photo.url}</div>
              </div>
            `;
          }}
        />
      </div>
      <div style={{ padding: 16 }}>
        <h3 style={{
          fontSize: 16,
          fontWeight: 600,
          color: currentThemeName === 'dark' ? 'white' : theme.text,
          margin: '0 0 8px 0'
        }}>
          {photo.name}
        </h3>
        <div style={{
          fontSize: 12,
          color: currentThemeName === 'dark' ? '#9ca3af' : theme.textSecondary,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8
        }}>
          <span>{photo.size}</span>
          <span>{photo.uploadDate}</span>
        </div>
        {Number.isInteger(photo.id) && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              onClick={() => handleChangePhotoClick(photo)}
              disabled={uploading && replacingId === photo.id}
              style={{
                padding: '8px 12px',
                borderRadius: 10,
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontWeight: 700
              }}
              aria-label={`Change photo for ${photo.name}`}
            >
              {uploading && replacingId === photo.id ? 'Changing...' : 'Change Photo'}
            </button>
            <button
              onClick={() => editPhoto(photo)}
              disabled={savingEdit}
              style={{
                padding: '8px 12px',
                borderRadius: 10,
                background: currentThemeName === 'dark' ? 'rgba(30, 41, 59, 0.6)' : (theme.cardBg || '#ffffff'),
                border: currentThemeName === 'dark' ? '1px solid rgba(59, 130, 246, 0.25)' : `1px solid ${theme.border}`,
                color: currentThemeName === 'dark' ? '#93c5fd' : theme.text,
                cursor: 'pointer',
                fontWeight: 600
              }}
              aria-label={`Edit ${photo.name}`}
            >
              {savingEdit ? 'Saving...' : 'Edit'}
            </button>
            <button
              onClick={() => deletePhoto(photo.id)}
              style={{
                padding: '8px 12px',
                borderRadius: 10,
                background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontWeight: 700
              }}
              aria-label={`Remove ${photo.name}`}
            >
              Remove
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Loading state
  // Loading state - MOVED INSIDE FUNCTION
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: currentThemeName === 'dark' ? '#0f172a' : currentThemeName === 'beige' ? '#f5f5dc' : '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader size={48} className="animate-spin" style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 18, fontWeight: 600, color: theme.text }}>Loading content...</div>
        </div>
      </div>
    );
  }

  // Main return statement - MOVED INSIDE FUNCTION
  return (
    <div style={{
      minHeight: '100vh',
      background: pageBackground,
      color: baseTextColor,
      padding: '24px 32px'
    }}>
      {/* Upload Popup */}
      {uploading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: currentThemeName === 'dark' ? 'rgba(30, 41, 59, 0.95)' : 'white',
            padding: '32px 48px',
            borderRadius: 16,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            textAlign: 'center',
            border: currentThemeName === 'dark' ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid #e5e7eb'
          }}>
            <Loader size={48} style={{ 
              marginBottom: 16, 
              color: '#3b82f6',
              animation: 'spin 1s linear infinite'
            }} />
            <div style={{ 
              fontSize: 18, 
              fontWeight: 700, 
              color: currentThemeName === 'dark' ? 'white' : theme.text,
              marginBottom: 8
            }}>
              {replacingId ? 'Changing Photo...' : 'Uploading Photo...'}
            </div>
            <div style={{ 
              fontSize: 14, 
              color: currentThemeName === 'dark' ? 'rgba(255,255,255,0.6)' : theme.textSecondary
            }}>
              Please wait...
            </div>
          </div>
        </div>
      )}

      {/* Sticky header like analytics */}
      <header style={{ 
        background: isDarkMode ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255,255,255,0.9)',
        borderBottom: isDarkMode ? '1px solid rgba(59, 130, 246, 0.2)' : `1px solid ${theme.border}`,
        backdropFilter: 'blur(20px)',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <div style={{ 
          maxWidth: 1400, 
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              onClick={() => navigate('/fullchat')}
              style={{
                padding: '10px 14px',
                borderRadius: 10,
                background: isDarkMode ? 'rgba(30, 41, 59, 0.6)' : surfaceBackground,
                border: isDarkMode ? '1px solid rgba(59, 130, 246, 0.25)' : `1px solid ${theme.border}`,
                color: isDarkMode ? '#93c5fd' : baseTextColor,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontWeight: 600
              }}
            >
              <ArrowLeft size={16} />
              {t('contentEdit.backToChat')}
            </button>
            <div>
              <div style={{ fontSize: 26, fontWeight: 700, color: baseTextColor }}>
                {t('contentEdit.contentEditor')}
              </div>
              <div style={{ fontSize: 13, color: mutedTextColor }}>
                {t('contentEdit.managePhotosDescription')}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {activeCategory === 'businessTypes' && (
              <button
                onClick={async () => {
                  const name = prompt('Enter category name:');
                  if (!name?.trim()) return;

                  try {
                    addLog(`Adding category: ${name}`, 'info');
                    await createCategory(name);
                    addLog('Category added successfully', 'success');
                    fetchPhotos(); // Refresh the list
                  } catch (err) {
                    addLog(`Error: ${err.message}`, 'error');
                    alert(`Error: ${err.message}`);
                  }
                }}
                style={{
                  padding: '12px 20px',
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
                }}
              >
                <Plus size={18} />
                Add Category
              </button>
            )}
            {activeCategory === 'businessHelpTopics' && (
              <button
                onClick={async () => {
                  const name = prompt('Enter topic name:');
                  if (!name?.trim()) return;

                  try {
                    addLog(`Adding topic: ${name}`, 'info');
                    await createTopic(name);
                    addLog('Topic added successfully', 'success');
                    fetchPhotos(); // Refresh the list
                  } catch (err) {
                    addLog(`Error: ${err.message}`, 'error');
                    alert(`Error: ${err.message}`);
                  }
                }}
                style={{
                  padding: '12px 20px',
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
                }}
              >
                <Plus size={18} />
                Add Topic
              </button>
            )}
            <button
              onClick={handleUploadClick}
              disabled={uploading}
              style={{
                padding: '12px 20px',
                borderRadius: 12,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: 'none',
                color: 'white',
                cursor: uploading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                opacity: uploading ? 0.6 : 1
              }}
            >
              <ImageIcon size={18} />
              {uploading ? 'Uploading...' : 'Upload Photo'}
            </button>
            <button
              onClick={fetchPhotos}
              style={{
                padding: '12px 16px',
                borderRadius: 12,
                background: currentThemeName === 'dark' ? 'rgba(30, 41, 59, 0.6)' : 'transparent',
                border: currentThemeName === 'dark' ? '1px solid rgba(59, 130, 246, 0.25)' : `1px solid ${theme.border}`,
                color: currentThemeName === 'dark' ? '#93c5fd' : theme.text,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontWeight: 600
              }}
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: 1400, margin: '0 auto', paddingTop: 20 }}>

        {/* Log Box */}
        <div style={{
          marginBottom: 16,
          border: currentThemeName === 'dark' ? '1px solid rgba(59,130,246,0.25)' : `1px solid ${theme.border}`,
          borderRadius: 12,
          background: currentThemeName === 'dark' ? 'rgba(12,20,40,0.6)' : (theme.cardBg || '#fff')
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px' }}>
            <div style={{ fontWeight: 700, color: currentThemeName === 'dark' ? '#93c5fd' : theme.text }}>Logs</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowLogs(v => !v)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(148,163,184,0.3)', background: 'transparent', color: currentThemeName === 'dark' ? '#cbd5e1' : theme.text, cursor: 'pointer' }}>
                {showLogs ? 'Hide' : 'Show'}
              </button>
              <button onClick={() => setLogs([])} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.4)', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}>
                Clear
              </button>
            </div>
          </div>
          {showLogs && (
            <div style={{ maxHeight: 220, overflow: 'auto', padding: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', fontSize: 12 }}>
              {logs.length === 0 && (
                <div style={{ opacity: 0.7 }}>No logs yet. Actions (Upload/Change/Edit/Delete/Refresh) will appear here.</div>
              )}
              {logs.map((l, i) => (
                <div key={i} style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'baseline',
                  color: l.level === 'error' ? (currentThemeName === 'dark' ? '#fecaca' : '#991b1b') : (currentThemeName === 'dark' ? '#e2e8f0' : '#334155')
                }}>
                  <span style={{ opacity: 0.6 }}>{new Date(l.ts).toLocaleTimeString()}</span>
                  <span style={{ fontWeight: 600, textTransform: 'uppercase', opacity: 0.8 }}>{l.level}</span>
                  <span>{l.msg}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Toolbar: Tabs on left, Search + View on right */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 24,
          borderBottom: `1px solid ${theme.border}`,
          paddingBottom: 16,
          flexWrap: 'wrap'
        }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                style={{
                  padding: '10px 14px',
                  borderRadius: 12,
                  background: activeCategory === category.id
                    ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                    : (currentThemeName === 'dark' 
                        ? 'rgba(30, 41, 59, 0.6)'
                        : (theme.cardBg || '#ffffff')),
                  color: activeCategory === category.id ? 'white' : (currentThemeName === 'dark' ? '#93c5fd' : theme.text),
                  border: activeCategory === category.id ? 'none' : (currentThemeName === 'dark' ? '1px solid rgba(59, 130, 246, 0.25)' : `1px solid ${theme.border}`),
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                  boxShadow: activeCategory === category.id ? '0 0 0 1px rgba(59,130,246,0.35) inset' : 'none'
                }}
              >
                {React.createElement(category.icon, { size: 16 })}
                {category.id === 'businessHelpTopics' ? t('contentEdit.businessHelpTopics') : t('contentEdit.businessTypes')}
                <span style={{
                  background: activeCategory === category.id 
                    ? 'rgba(255,255,255,0.2)'
                    : (isDarkMode ? 'rgba(255,255,255,0.06)' : (theme.backgroundSecondary || '#f1f5f9')),
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 12,
                  color: baseTextColor
                }}>
                  {getCategoryCount(category.id)}
                </span>
              </button>
            ))}
          </div>

          {/* Right controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', width: 360, maxWidth: '100%' }}>
              <Search size={16} style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: isDarkMode ? '#93a4c4' : theme.textSecondary
              }} />
              <input
                type="text"
                placeholder="Search photos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  height: 40,
                  padding: '10px 12px 10px 40px',
                  borderRadius: 12,
                  border: currentThemeName === 'dark' ? '1px solid rgba(59,130,246,0.35)' : `1px solid ${theme.border}`,
                  background: currentThemeName === 'dark' ? 'rgba(12, 20, 40, 0.9)' : (theme.backgroundSecondary || '#f8fafc'),
                  color: baseTextColor,
                  fontSize: 14,
                  boxShadow: currentThemeName === 'dark' 
                    ? '0 0 0 1px rgba(59,130,246,0.25), 0 2px 8px rgba(0,0,0,0.25) inset' 
                    : '0 1px 2px rgba(0,0,0,0.04) inset',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setViewMode('grid')}
                title="Grid view"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: viewMode === 'grid' 
                    ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' 
                    : (currentThemeName === 'dark' ? 'rgba(30, 41, 59, 0.6)' : (theme.cardBg || '#ffffff')),
                  color: viewMode === 'grid' ? 'white' : (currentThemeName === 'dark' ? '#93c5fd' : theme.text),
                  border: currentThemeName === 'dark' ? '1px solid rgba(59,130,246,0.25)' : `1px solid ${theme.border}`,
                  cursor: 'pointer'
                }}
              >
                <Grid size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                title="List view"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: viewMode === 'list' 
                    ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' 
                    : (currentThemeName === 'dark' ? 'rgba(30, 41, 59, 0.6)' : (theme.cardBg || '#ffffff')),
                  color: viewMode === 'list' ? 'white' : (currentThemeName === 'dark' ? '#93c5fd' : theme.text),
                  border: currentThemeName === 'dark' ? '1px solid rgba(59,130,246,0.25)' : `1px solid ${theme.border}`,
                  cursor: 'pointer'
                }}
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Business Help Topics Section */}
        {activeCategory === 'businessHelpTopics' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
            <div>
              <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 12px' }}>{t('contentEdit.businessHelpTopics')}</h2>
              <p style={{ margin: '0 0 24px', color: currentThemeName === 'dark' ? '#9ca3af' : theme.textSecondary }}>
                {t('contentEdit.businessHelpTopicsDescription')}
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: viewMode === 'grid'
                  ? 'repeat(auto-fill, minmax(320px, 1fr))'
                  : '1fr',
                gap: 24
              }}>
                {filteredTopicEntries.map(({ topic, photo, hasPhoto }) => (
                  <div
                    key={topic.id}
                    style={{
                      borderRadius: 16,
                      border: currentThemeName === 'dark' ? '1px solid rgba(59,130,246,0.25)' : `1px solid ${theme.border}`,
                      background: currentThemeName === 'dark' ? 'rgba(12,20,40,0.6)' : (theme.cardBg || '#fff'),
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: theme.text }}>{topic.name}</div>
                          {topic.description && (
                            <div style={{ fontSize: 13, color: currentThemeName === 'dark' ? '#94a3b8' : theme.textSecondary }}>
                              {topic.description}
                            </div>
                          )}
                        </div>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '6px 12px',
                          borderRadius: 999,
                          background: hasPhoto ? 'rgba(34,197,94,0.15)' : 'rgba(248,113,113,0.15)',
                          color: hasPhoto ? '#16a34a' : '#dc2626',
                          fontSize: 12,
                          fontWeight: 600
                        }}>
                          {hasPhoto ? 'Photo linked' : 'Photo missing'}
                        </span>
                      </div>

                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => {
                            if (photo) {
                              handleChangePhotoClick(photo);
                            } else {
                              handleTopicPhotoUpload({ topic, photo });
                            }
                          }}
                          disabled={uploading}
                          style={{
                            padding: '10px 14px',
                            borderRadius: 12,
                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                            border: 'none',
                            color: 'white',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            fontWeight: 600
                          }}
                        >
                          {photo ? 'Replace Photo' : 'Upload Photo'}
                        </button>

                        <button
                          onClick={() => {
                            setPendingTopicName(topic.name);
                            handleTopicPhotoUpload({ topic, photo });
                          }}
                          disabled={uploading}
                          style={{
                            padding: '10px 14px',
                            borderRadius: 12,
                            background: currentThemeName === 'dark' ? 'rgba(30,41,59,0.55)' : (theme.cardBg || '#f8fafc'),
                            border: currentThemeName === 'dark' ? '1px solid rgba(59, 130, 246, 0.35)' : `1px solid ${theme.border}`,
                            color: theme.text,
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            fontWeight: 600
                          }}
                        >
                          Assign Existing Photo
                        </button>
                      </div>
                    </div>

                    {photo ? (
                      <PhotoDisplay photo={photo} currentThemeName={currentThemeName} theme={theme} />
                    ) : (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 220,
                        background: currentThemeName === 'dark' ? 'rgba(15,23,42,0.65)' : '#f1f5f9',
                        color: currentThemeName === 'dark' ? '#94a3b8' : '#475569'
                      }}>
                        <ImageIcon size={40} style={{ marginBottom: 12, opacity: 0.5 }} />
                        <div style={{ fontSize: 14, fontWeight: 600 }}>No photo attached</div>
                        <div style={{ fontSize: 12, opacity: 0.75 }}>Use the buttons above to upload or assign a photo for this topic.</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {filteredTopicEntries.length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '80px 20px',
                  color: currentThemeName === 'dark' ? '#8aa0c7' : theme.textSecondary
                }}>
                  <ImageIcon size={56} style={{ marginBottom: 16, opacity: 0.4 }} />
                  <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, color: currentThemeName === 'dark' ? '#d0defa' : theme.text }}>
                    No topics match your search
                  </h3>
                  <p style={{ margin: 0, opacity: 0.9 }}>Try adjusting the search term or add a new topic.</p>
                </div>
              )}
            </div>

          </div>
        )}

        {/* Business Types gallery */}
        {activeCategory === 'businessTypes' && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: viewMode === 'grid'
              ? 'repeat(auto-fill, minmax(280px, 1fr))'
              : '1fr',
            gap: 24
          }}>
            {filteredBusinessPhotos.map(photo => (
              <PhotoCard key={`${photo.id}-${photo.updatedAt || ''}`} photo={photo} />
            ))}
          </div>
        )}

        {activeCategory === 'businessTypes' && filteredBusinessPhotos.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '80px 20px',
            color: currentThemeName === 'dark' ? '#8aa0c7' : theme.textSecondary
          }}>
            <ImageIcon size={56} style={{ marginBottom: 16, opacity: 0.4 }} />
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, color: currentThemeName === 'dark' ? '#d0defa' : theme.text }}>
              No business type photos match your search
            </h3>
            <p style={{ margin: 0, opacity: 0.9 }}>Try a different search term or upload new photos.</p>
          </div>
        )}

        {/* Hidden file input for uploads */}
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        {/* Hidden file input for replacing a photo */}
        <input
          type="file"
          accept="image/*"
          ref={replaceFileInputRef}
          onChange={handleReplaceFileChange}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
}

export default ContentEditPage;
